import { z } from "zod";
import { EntityType } from "../entityType.js";
import { assistantsSources } from "./Convo.js";

export const RecommendationTypeSchema = z.enum([
  "assistant-prompt",
  "template-prompt",
  "actionable-prompt",
]);

export const RecommendationContextKeySchema = z.enum([
  ...assistantsSources,
  "workspace-onboarding",
  "assistant",
]);

export type RecommendationContextKey = z.infer<
  typeof RecommendationContextKeySchema
>;

export const BaseRecommendationSchema = z.object({
  id: z.string(),
  label: z.string(),
  contextKey: z.array(RecommendationContextKeySchema),
  entityType: z.enum(EntityType),
});

export const AssistantPromptPayloadSchema = z.object({
  type: z.literal("assistant-prompt"),
  prompt: z.string(),
  includeContext: z
    .object({
      facts: z.boolean().optional(),
      currentPage: z.boolean().optional(),
      recentActivity: z.boolean().optional(),
    })
    .optional(),
});

export type AssistantPromptPayload = z.infer<
  typeof AssistantPromptPayloadSchema
>;

export const ActionablePromptPayloadSchema = z.object({
  type: z.literal("actionable-prompt"),
  prompt: z.string(),
});

export type ActionablePromptPayload = z.infer<
  typeof ActionablePromptPayloadSchema
>;

export const TemplatePromptPayloadSchema = z.object({
  type: z.literal("template-prompt"),
  prompt: z.string(),
  action: z.enum([
    "update-fact",
    "create-task",
    "create-note",
    "create-contact",
    "create-tag",
    "list-documents",
  ]),
});

export type TemplatePromptPayload = z.infer<typeof TemplatePromptPayloadSchema>;

export const RecommendationPayloadSchema = z.discriminatedUnion("type", [
  AssistantPromptPayloadSchema,
  ActionablePromptPayloadSchema,
  TemplatePromptPayloadSchema,
]);

export type RecommendationPayload = z.infer<typeof RecommendationPayloadSchema>;

export const RecommendationSchema = z.object({
  ...BaseRecommendationSchema.shape,
  payload: RecommendationPayloadSchema,
});

export type Recommendation = z.infer<typeof RecommendationSchema>;

export const CreateRecommendationInputSchema = RecommendationSchema.omit({
  id: true,
});

export type CreateRecommendationInput = z.infer<
  typeof CreateRecommendationInputSchema
>;

export const GetRecommendationsInputSchema = z.object({
  contextKey: RecommendationContextKeySchema.optional(),
  types: z.array(RecommendationTypeSchema).optional(),
  limit: z.number().min(1).max(100).optional(),
});

export type GetRecommendationsInput = z.infer<
  typeof GetRecommendationsInputSchema
>;
